import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import './MarkdownRenderer.css';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className = '' }) => {
  return (
    <div className={`markdown-renderer ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Code blocks
          code({ className, children, ...props }: any) {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';
            const inline = !match;
            
            return !inline && match ? (
              <SyntaxHighlighter
                style={vscDarkPlus as any}
                language={language}
                PreTag="div"
                className="code-block"
                {...props}
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            ) : (
              <code className={`inline-code ${className || ''}`} {...props}>
                {children}
              </code>
            );
          },
          
          // Headers
          h1: ({ children }: any) => (
            <h1 className="markdown-h1">{children}</h1>
          ),
          h2: ({ children }: any) => (
            <h2 className="markdown-h2">{children}</h2>
          ),
          h3: ({ children }: any) => (
            <h3 className="markdown-h3">{children}</h3>
          ),
          h4: ({ children }: any) => (
            <h4 className="markdown-h4">{children}</h4>
          ),
          
          // Lists
          ul: ({ children }: any) => (
            <ul className="markdown-ul">{children}</ul>
          ),
          ol: ({ children }: any) => (
            <ol className="markdown-ol">{children}</ol>
          ),
          li: ({ children }: any) => (
            <li className="markdown-li">{children}</li>
          ),
          
          // Tables
          table: ({ children }: any) => (
            <div className="table-wrapper">
              <table className="markdown-table">{children}</table>
            </div>
          ),
          thead: ({ children }: any) => (
            <thead className="markdown-thead">{children}</thead>
          ),
          tbody: ({ children }: any) => (
            <tbody className="markdown-tbody">{children}</tbody>
          ),
          tr: ({ children }: any) => (
            <tr className="markdown-tr">{children}</tr>
          ),
          th: ({ children }: any) => (
            <th className="markdown-th">{children}</th>
          ),
          td: ({ children }: any) => (
            <td className="markdown-td">{children}</td>
          ),
          
          // Blockquotes
          blockquote: ({ children }: any) => (
            <blockquote className="markdown-blockquote">{children}</blockquote>
          ),
          
          // Links
          a: ({ href, children }: any) => (
            <a
              href={href}
              className="markdown-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              {children}
            </a>
          ),
          
          // Paragraphs
          p: ({ children }: any) => (
            <p className="markdown-p">{children}</p>
          ),
          
          // Strong/Bold
          strong: ({ children }: any) => (
            <strong className="markdown-strong">{children}</strong>
          ),
          
          // Emphasis/Italic
          em: ({ children }: any) => (
            <em className="markdown-em">{children}</em>
          ),
          
          // Horizontal rule
          hr: () => <hr className="markdown-hr" />,
          
          // Images
          img: ({ src, alt }: any) => (
            <img 
              src={src} 
              alt={alt} 
              className="markdown-img"
              loading="lazy"
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
